import Modal, {ModalFooter} from '../shared/Modal.js';
import Badge from '../shared/Badge.jsx';
import {useAppNavigate} from '../app/navigation/index.js';
import './poultryDesign.css';
import Button from '../shared/Button.js';
import Segmented from '../shared/Segmented.js';
import {PillGroup} from '../shared/GroupPillPicker.js';
import {dialogFooterStyle} from '../lib/dialogStyles.js';
// ============================================================================
// src/broiler/BatchForm.tsx  —  Phase 2 Round 6 tail
// ----------------------------------------------------------------------------
// The broiler add/edit modal. The last inline block that lived inside App's
// render body (`if(showForm) return ( ... )`). Hook-based extraction: every
// operational helper (upd/closeForm/submit/del/openEdit/parseProcessorXlsx/
// persist) still lives in App and arrives as a prop. `confirmDelete` is read
// from ConfirmContext here rather than threaded: B2.2c moved the overlays into
// the provider, so no mount supplies it as a prop any more. Nothing
// in the JSX body changes — derived values (tl, targetHatch, etc.) that
// used to live at App scope are recomputed here since this is the only
// consumer.
//
// THE DAILY HISTORY IS THIS COMPONENT'S OWN READ. broiler.batchForm.dailys owns
// it for BOTH surfaces that mount this file — the full-screen form App
// dispatches on `showForm`, and the embedded copy BroilerBatchPage renders —
// because a catalog resource may only name surfaces the manifest declares
// migrated, so one of them could not have moved alone.
//
// WHAT IS STILL BatchesContext, AND WHY. The batch DRAFT (`form`, `editId`,
// `conflicts`, `parsedProcessor`, `showLegacy`, `docUploading`, `formNotice`)
// is App render state, filled by App's openAdd/openEdit and never by a server
// read. It is not a boot value and no residual id names it.
//
// `batches` IS GONE, AND WITH IT `legacy.context.batches.batches` ON THIS
// COMPONENT. It was never rendered here. It was held for one purpose — to be
// the base of `batchRows.map((b) => (b.id === editId ? {...b, documents} : b))`
// in the three document handlers, on the way to a `persist` that replaces the
// whole ppp-v4 blob — so the argument for keeping it was that a mount-time
// snapshot would revert field saves that landed after the form opened. That
// argument was about the WRITE BASE, and the write base moved: App's `persist`
// merges onto a save-time read of the blob (src/lib/broilerBatchesApi.ts), so
// no caller has to supply a current array any more. The handlers name their row
// instead, through the `persistDocuments` prop, and this component holds no
// batch array at all.
//
// That is strictly better than the old path on the case that motivated it: with
// no legacy boot, `batchRows` was `[]`, the map matched nothing, and the
// document reached storage and then vanished from the blob without a word. The
// patch is applied to the merged base now, so it lands on the row wherever the
// row came from, and refuses out loud when it matches none.
// ============================================================================
import React, {type CSSProperties, type ReactNode, type SVGProps} from 'react';
import {sb} from '../lib/supabase.js';
import {fmtDate as fmt, fmtDate as fmtMDY, todayCentralISO} from '../lib/dateUtils.js';
import {S, getReadableText} from '../lib/styles.js';
import {getProgramColor} from '../lib/programColors.js';
import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {recordControl, recordTextarea, recordFieldLabel, recordCheckbox} from '../shared/recordPageControls.jsx';
import {
  BROODERS,
  SCHOONERS,
  STATUSES,
  ALL_HATCHERIES,
  LEGACY_HATCHERIES,
  LEGACY_BREEDS,
  isNearHoliday,
  calcTargetHatch,
  suggestHatchDates,
  calcTimeline,
  resolveBrooderEntryDate,
  growoutWeeksFor,
  BROODER_DAYS_MIN,
  BROODER_DAYS_MAX,
  validBrooderDays,
  isInvalidBrooderDays,
  isWeekendSchoonerMove,
  calcBroilerStatsFromDailys,
} from '../lib/broiler.js';
// `BroilerBatch` left this import with `BroilerBatchRow`: the only thing this
// component used it for was the intersection that typed App's ppp-v4 array, and
// the array is gone. What is still imported is the daily-history row and the
// timeline, both of which this file computes and renders itself.
import type {BroilerDailyRow, BroilerTimeline} from '../lib/broiler.js';
import type {ParsedProcessorSheet} from '../lib/processorXlsx.js';
import type {Notice} from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {PrivateStorageLink} from '../shared/PrivateStorageObject.jsx';
import {useBatches} from '../contexts/BatchesContext.jsx';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {useFeedCosts} from '../contexts/FeedCostsContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerBatchFormDailysData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';

type BatchDocument = {
  name?: string | null;
  path?: string | null;
  size?: number | null;
  uploadedAt?: string | null;
  url?: string | null;
};
type BatchFormDraft = {
  name: string;
  breed: string;
  hatchery: string;
  hatchDate: string;
  birdCount: string | number;
  birdCountActual: string | number;
  brooder: string;
  schooner: string;
  processingDate: string;
  status: string;
  notes: string;
  brooderIn: string;
  brooderOut: string;
  brooderFeedLbs: string | number;
  schoonerFeedLbs: string | number;
  gritLbs: string | number;
  mortalityCumulative: string | number;
  week4Lbs: string | number;
  week6Lbs: string | number;
  perLbStandardCost: string | number;
  perLbStarterCost: string | number;
  perLbGritCost: string | number;
  chickCost: string | number;
  totalToProcessor: string | number;
  processingCost: string | number;
  avgBreastLbs: string | number;
  avgThighsLbs: string | number;
  avgDressedLbs: string | number;
  totalLbsWhole: string | number;
  totalLbsCuts: string | number;
  documents: BatchDocument[];
  growoutWeeks?: number | string | null;
  brooderDays?: number | string | null;
  [key: string]: string | number | BatchDocument[] | null | undefined;
};
// `BroilerBatchRow` USED TO LIVE HERE and is gone rather than merely unused. It
// existed to type `batches as BroilerBatchRow[]` — the cast that turned App's
// ppp-v4 array into something the three document handlers could map over. The
// handlers name their row now, so this component types no batch ROW at all; the
// draft it does hold is `BatchFormDraft`.

// Module-level, not an inline literal: `broilerDailys` is read by three
// separate render blocks below and a fresh `[]` per pre-resolution render would
// make each of them a new array identity for no reason.
const NO_BROILER_DAILYS: BroilerDailyRow[] = [];
// Frozen for the same reason LayersView freezes its own: useRouteResource
// serializes `params` into its memo key, so a new object literal per render
// would churn it.
const BROILER_BATCH_FORM_DAILYS_PARAMS = Object.freeze({});
type ProcessorMetricKey = 'avgDressed' | 'avgBreast' | 'avgThigh' | 'totalLbsWhole' | 'totalLbsCuts';
type ProcessorField = {
  key: ProcessorMetricKey;
  label: string;
  val: number | undefined;
  fmt: (value: number | undefined) => ReactNode;
};
type BatchFormProps = {
  upd: (key: string, value: string) => void;
  closeForm: () => Promise<boolean>;
  submit: (force: boolean) => Promise<boolean>;
  del: (id: string, onDeleted?: () => void) => void;
  openEdit: (batch: unknown) => void;
  parseProcessorXlsx: (file: File) => Promise<void>;
  // The ONLY ppp-v4 write this component performs, and it names a row rather
  // than replacing an array. There is deliberately no `persist` prop here any
  // more: a whole-array saver is the thing this component had to hold App's
  // boot-loaded array to feed, and taking the array away while leaving the
  // saver would have made the read reappear the first time anyone touched the
  // document handlers. See src/App.tsx's persistDocuments.
  persistDocuments: (batchId: string | null, documents: unknown[]) => Promise<boolean>;
  onClose?: () => void | Promise<void>;
  onDeleted?: () => void;
  embedded?: boolean;
  hostedByPage?: boolean;
  timelinePreview?: ReactNode;
  weighInWeekAverages?: {week4Lbs: number | null; week6Lbs: number | null} | null;
};

// The independently released full-screen mount is deliberately narrower than
// the embedded record-page contract. These are the exact App closures the form
// has always received; `onClose`, `embedded`, and weigh-in presentation state
// remain owned by the record page and cannot leak into the release bridge.
export type BatchFormFullscreenActions = Pick<
  BatchFormProps,
  'upd' | 'closeForm' | 'submit' | 'del' | 'openEdit' | 'parseProcessorXlsx' | 'persistDocuments'
>;

const weighInSourcedValueBox: CSSProperties = {
  ...recordControl,
  background: 'var(--surface-2)',
  borderColor: 'var(--border)',
  color: 'var(--ink)',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  maxWidth: 'none',
  minHeight: 42,
};

const weighInSourcedHint: CSSProperties = {fontSize: 11, color: 'var(--ink-faint)', marginTop: 3};
const readOnlyValueBox: CSSProperties = {
  ...weighInSourcedValueBox,
  justifyContent: 'space-between',
  color: 'var(--text-primary)',
};

const broilerControl: CSSProperties = {
  ...recordControl,
  maxWidth: 'none',
  minHeight: 42,
  padding: '8px 12px',
  fontWeight: 600,
  color: 'var(--text-primary)',
  background: 'white',
};

const broilerTextarea: CSSProperties = {
  ...recordTextarea,
  maxWidth: 'none',
  minHeight: 86,
  padding: '10px 12px',
  fontWeight: 500,
};

const broilerLabel: CSSProperties = {...recordFieldLabel, fontSize: 12, color: 'var(--ink-muted)'};
const broilerHelp: CSSProperties = {fontSize: 12, color: 'var(--ink-faint)', lineHeight: 1.4, marginTop: 3};
// The Add Batch schedule block: a quiet panel, not a card, so it reads as a
// consequence of the fields above rather than a separate thing to fill in.
const schedulePreviewS: CSSProperties = {
  display: 'grid',
  gap: 6,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
};
const schedulePreviewRowS: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 12,
  fontSize: 13,
  color: 'var(--ink)',
};
// The Days in brooder field follows Constitution §3/§4 Forms (I10/I11) on its
// own; the surrounding fields keep the shared broiler styles above.
const brooderDaysLabel: CSSProperties = {...recordFieldLabel, fontSize: 13, fontWeight: 600};
const brooderDaysControl: CSSProperties = {
  ...broilerControl,
  minHeight: 44,
  fontSize: 15,
  padding: '10px 12px',
  border: '1px solid var(--border-strong)',
  borderRadius: 12,
};
const brooderDaysControlInvalid: CSSProperties = {...brooderDaysControl, border: '1px solid var(--danger-ink)'};
const brooderDaysHelp: CSSProperties = {fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: 4};
const brooderDaysError: CSSProperties = {...brooderDaysHelp, color: 'var(--danger-ink)', fontWeight: 600};
// Soft: warn ink, never danger, never blocks.
const brooderDaysWarning: CSSProperties = {...brooderDaysHelp, color: 'var(--warn-ink)'};
const broilerGrid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 24,
  rowGap: 18,
};
const broilerGrid3: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: 24,
  rowGap: 18,
};
const sectionCard: CSSProperties = {
  background: 'white',
  border: '1px solid transparent',
  borderRadius: 14,
  boxShadow: '0 6px 22px rgba(20,30,40,.09)',
  overflow: 'hidden',
};
const sectionHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '16px 22px',
  borderBottom: '1px solid var(--divider)',
};
const sectionIcon: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  background: 'var(--surface-2)',
  color: 'var(--ink-muted)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
};
const sectionBody: CSSProperties = {padding: 22};
const stepBadge: CSSProperties = {
  width: 21,
  height: 21,
  borderRadius: 999,
  background: 'var(--surface-2)',
  color: 'var(--ink-muted)',
  fontSize: 11,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
};
const timelinePanel: CSSProperties = {
  marginTop: 22,
  background: 'var(--bg-page)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '18px 20px',
};

function SectionIcon({type}: {type: string}) {
  const common = {
    width: 17,
    height: 17,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  } satisfies SVGProps<SVGSVGElement>;
  return (
    <span style={sectionIcon} aria-hidden="true">
      {type === 'schedule' && (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      )}
      {type === 'details' && (
        <svg {...common}>
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M9 11h6M9 15h4" />
        </svg>
      )}
      {type === 'housing' && (
        <svg {...common}>
          <path d="M3 11l9-7 9 7M5 10v10h14V10M9 20v-6h6v6" />
        </svg>
      )}
      {type === 'feed' && (
        <svg {...common}>
          <path d="M12 22V12M12 12c0-4 3-7 8-7 0 4-3 7-8 7zM12 14c0-3-2.5-5-7-5 0 3 2.5 5 7 5z" />
        </svg>
      )}
      {type === 'processing' && (
        <svg {...common}>
          <path d="M12 2l9 5-9 5-9-5 9-5zM3 12l9 5 9-5M3 17l9 5 9-5" />
        </svg>
      )}
      {type === 'totals' && (
        <svg {...common}>
          <path d="M4 20V10M10 20V4M16 20v-7M21 20H3" />
        </svg>
      )}
      {type === 'documents' && (
        <svg {...common}>
          <path d="M21 12.5l-8.5 8.5a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.6 1.6 0 0 1-2.3-2.3l7.8-7.8" />
        </svg>
      )}
    </span>
  );
}

function SectionCard({
  icon,
  title,
  hint,
  children,
  bodyStyle,
}: {
  icon: string;
  title: string;
  hint?: ReactNode;
  children?: ReactNode;
  bodyStyle?: CSSProperties;
}) {
  return (
    <section style={sectionCard} data-broiler-record-section={title}>
      <div style={sectionHeader}>
        <SectionIcon type={icon} />
        <h2 style={{fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0}}>{title}</h2>
        {hint && <span style={{fontSize: 12, color: 'var(--ink-faint)', marginLeft: 2}}>{hint}</span>}
      </div>
      <div
        data-mobile-1col={bodyStyle ? '1' : undefined}
        style={bodyStyle ? {...sectionBody, ...bodyStyle} : sectionBody}
      >
        {children}
      </div>
    </section>
  );
}

function StepHeading({number, children, style}: {number: ReactNode; children?: ReactNode; style?: CSSProperties}) {
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14, ...style}}>
      <span style={stepBadge}>{number}</span>
      <span style={{fontSize: 13, fontWeight: 700, color: 'var(--text-primary)'}}>{children}</span>
    </div>
  );
}

// Whole days between two date-only ISO strings, read at local noon.
function daysBetween(fromISO: string, toISO: string): number {
  return Math.round(
    (new Date(toISO.slice(0, 10) + 'T12:00:00').getTime() - new Date(fromISO.slice(0, 10) + 'T12:00:00').getTime()) /
      86400000,
  );
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function TimelinePanel({timeline, hatchDate}: {timeline?: BroilerTimeline | null; hatchDate: string}) {
  if (!timeline) return null;
  const items = [
    {
      label: 'Brooder In',
      date: timeline.brooderIn,
      note: timeline.brooderIn === hatchDate ? 'Hatch day' : 'Recorded entry',
    },
    {
      label: 'Brooder Out',
      date: timeline.brooderOut,
      note: daysBetween(hatchDate, timeline.brooderOut) + ' days after hatch',
    },
    {label: 'Schooner In', date: timeline.schoonerIn, note: 'Move to pasture'},
    {label: 'Schooner Out', date: timeline.schoonerOut, note: 'Ready to process', final: true},
  ];
  const today = todayCentralISO();
  return (
    <section>
      <h2 style={{fontSize: 18, margin: '0 0 8px'}}>Projected Timeline</h2>
      <div className="poultry-phase-strip">
        {items.map((item, index) => (
          <div key={item.label} className="poultry-phase">
            <span
              aria-hidden="true"
              className="poultry-phase-dot"
              data-current={Boolean(
                item.date && item.date <= today && (!items[index + 1] || items[index + 1].date > today),
              )}
            />
            <div>
              <div className="poultry-phase-label">{item.label}</div>
              <div className="poultry-meta">{fmt(item.date)}</div>
              <div className="poultry-meta">{item.note}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function broilerWeekWeightLabel(value: string | number | null | undefined) {
  const parsed = Number.parseFloat(value as string);
  if (!Number.isFinite(parsed) || parsed <= 0) return 'Not recorded';
  return Math.round(parsed * 100) / 100 + ' lbs';
}

function broilerRecordedWeekWeight(...values: Array<string | number | null | undefined>) {
  for (const value of values) {
    const parsed = Number.parseFloat(value as string);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

export default function BatchForm({
  upd,
  closeForm,
  submit,
  del,
  openEdit: _openEdit,
  parseProcessorXlsx,
  persistDocuments,
  // Optional close override for record-page mode. When provided, the close
  // handlers route through this instead of the closeForm+setShowForm dance —
  // keeps the URL the source of truth on BroilerBatchPage. Prev/Next is owned
  // by the shared RecordSequenceNav on the record page, not BatchForm.
  onClose,
  onDeleted,
  // When true, parent (BroilerBatchPage) already renders Header and supplies
  // its own page chrome; BatchForm skips its own Header render and drops the
  // semi-transparent modal-overlay background so the form sits flush in the
  // record-page layout.
  embedded = false,
  hostedByPage = false,
  timelinePreview = null,
  weighInWeekAverages = null,
}: BatchFormProps) {
  const {confirmDelete, confirmActionPrompt} = useConfirm();
  const navigate = useAppNavigate();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const batchContext = useBatches();
  const {conflicts, showLegacy, setShowLegacy, setParsedProcessor, docUploading, setDocUploading, setFormNotice} =
    batchContext;
  const editId = batchContext.editId as string | null;
  const form = batchContext.form as BatchFormDraft;
  // A create surface mounts with App's seeded draft. Suggested-date buttons
  // update that draft without an input event, so the modal needs its baseline.
  const createBaseline = React.useRef(JSON.stringify(form));
  const parsedProcessor = batchContext.parsedProcessor as ParsedProcessorSheet | null;
  const formNotice = batchContext.formNotice as Notice | null;
  const setForm = (next: React.SetStateAction<BatchFormDraft>) => {
    batchContext.setForm((previous: unknown) => {
      const current = previous as BatchFormDraft;
      return typeof next === 'function' ? next(current) : next;
    });
  };
  // The daily history is this form's own read now — broiler.batchForm.dailys
  // owns it for BOTH surfaces that mount this component, where
  // DailysRecentContext used to hand it down from App's boot. It is the ONLY
  // data this component reads: the destructure above takes nothing but draft
  // state and setters off BatchesContext, which is what empties
  // `legacy.context.batches.batches` on broiler.batchForm.fullscreen. See
  // src/app/data/migratedSurfaces/broiler.batchForm.fullscreen.ts.
  const dailysResource = useRouteResource(RESOURCE.BROILER_BATCH_FORM_DAILYS, BROILER_BATCH_FORM_DAILYS_PARAMS, {
    enabled: embedded || Boolean(editId),
  });
  const dailysData = dailysResource.data as BroilerBatchFormDailysData | null;
  useFeedCosts();
  const broilerDailys = (dailysData ? (dailysData.broilerDailys as BroilerDailyRow[]) : null) ?? NO_BROILER_DAILYS;
  // Whether the history has SETTLED, which is a different question from whether
  // it is empty — see the Feed & Grit notice below.
  const broilerDailysLoading = dailysResource.loading;

  // Derived values (were at App scope; only this component consumed them).
  const tl = calcTimeline(form.hatchDate, form.breed, form.processingDate, form.growoutWeeks, form.brooderDays);
  const targetHatch = calcTargetHatch(form.processingDate, form.breed, form.growoutWeeks);
  const hatchSuggestions = suggestHatchDates(targetHatch);
  const hatchWarn = isNearHoliday(form.hatchDate);
  const procWarn = form.processingDate && isNearHoliday(form.processingDate);
  const lockedBrooderIn = resolveBrooderEntryDate(form.hatchDate, form.brooderIn);
  // A draft that carries brooderDays shows the calculated brooder-out date, so a
  // stored date cannot mask a brooder-length edit. Legacy drafts (no
  // brooderDays) keep showing their stored date first, as before.
  const lockedBrooderOut =
    validBrooderDays(form.brooderDays) !== undefined ? tl?.brooderOut || '' : form.brooderOut || tl?.brooderOut || '';
  const brooderDaysInvalid = isInvalidBrooderDays(form.brooderDays);
  const brooderDaysChoice = brooderDaysInvalid
    ? String(form.brooderDays)
    : String(validBrooderDays(form.brooderDays) ?? BROODER_DAYS_MIN);
  const brooderDurationDays = tl ? daysBetween(tl.brooderIn, tl.brooderOut) : null;
  const weekendSchoonerMove = tl ? isWeekendSchoonerMove(tl.schoonerIn) : false;
  const week4WeightDisplay = broilerRecordedWeekWeight(weighInWeekAverages?.week4Lbs, form.week4Lbs);
  const week6WeightDisplay = broilerRecordedWeekWeight(weighInWeekAverages?.week6Lbs, form.week6Lbs);
  // Legacy hatcheries appended only when admin toggles "Show legacy" on a
  // processed batch.
  const hatcheries =
    form.status === 'processed' && showLegacy ? [...ALL_HATCHERIES, '__SEP__', ...LEGACY_HATCHERIES] : ALL_HATCHERIES;

  // App owns validation and confirmed persistence. Open the record only
  // after the create is confirmed; a refusal keeps the draft here.
  async function createAndOpen(force: boolean) {
    if (await submit(force)) navigate('/broiler/batches/' + encodeURIComponent(form.name));
  }
  // A hard conflict is a real double-booking; a soft one is advisory.
  const createHasHardConflict = conflicts.some((conflict) => !conflict.soft);

  if (!embedded && !editId)
    return (
      <>
        {!hostedByPage && <Header />}
        <Modal
          title="New Broiler Batch"
          dirty={createBaseline.current !== JSON.stringify(form)}
          size="sm"
          onClose={() => {
            if (onClose) onClose();
            else closeForm();
          }}
          dialogAttrs={{'data-broiler-form-mode': 'create', 'data-broiler-create': '1'}}
          footer={
            /* One primary action. A HARD booking conflict flips it to Save
               Anyway rather than adding a second button beside it, so the
               modal never offers two ways to commit the same draft. The
               confirm prompt is unchanged, so overriding a conflict still
               takes a deliberate second step. Soft conflicts are warnings
               only and leave the primary alone. */
            <ModalFooter
              primaryLabel={createHasHardConflict ? 'Save Anyway' : 'Create & Open'}
              primaryAttrs={{'data-broiler-create-primary': createHasHardConflict ? 'force' : 'normal'}}
              onPrimary={() =>
                createHasHardConflict
                  ? confirmActionPrompt(
                      'Create this batch with the listed booking conflicts?',
                      () => createAndOpen(true),
                      'Save Anyway',
                    )
                  : createAndOpen(false)
              }
            />
          }
        >
          <InlineNotice notice={formNotice} onDismiss={() => setFormNotice(null)} />
          <div style={{display: 'grid', gap: 12}}>
            <label style={broilerLabel}>
              Batch Name *
              <input
                aria-label="Batch Name"
                style={broilerControl}
                value={form.name}
                onChange={(event) => upd('name', event.target.value)}
              />
            </label>
            <div style={broilerLabel}>
              Breed
              {/* Each breed carries its hatch-to-processing length, which is
                  what actually differs between them (growoutWeeksFor is the
                  same helper the schedule uses). */}
              <Segmented
                aria-label="Breed"
                options={[
                  {value: 'CC', label: `Cornish Cross · ${growoutWeeksFor('CC', form.growoutWeeks)} wk`},
                  {value: 'WR', label: `White Ranger · ${growoutWeeksFor('WR', form.growoutWeeks)} wk`},
                ]}
                value={form.breed}
                onChange={(next) => upd('breed', next)}
              />
            </div>
            <div style={broilerLabel}>
              Hatchery
              {/* Pills, not a Segmented bar: four hatchery names will not
                  compress into equal segments at modal width. So Big Farms
                  stamps a longer grow-out, so the caption follows the pick. */}
              <PillGroup
                label="Hatchery"
                options={ALL_HATCHERIES.map((hatchery) => ({value: hatchery, label: hatchery}))}
                value={form.hatchery}
                onChange={(next) => upd('hatchery', next)}
                rootAttrs={{'data-broiler-hatchery-pills': '1'}}
              />
              <span className="poultry-meta">{growoutWeeksFor(form.breed, form.growoutWeeks)}-week grow-out</span>
            </div>
            <label style={broilerLabel}>
              Processing Date
              <input
                aria-label="Processing Date"
                style={broilerControl}
                type="date"
                value={form.processingDate}
                onChange={(event) => upd('processingDate', event.target.value)}
              />
            </label>
            {procWarn && (
              <div style={{color: 'var(--warn-ink)', fontSize: 12}}>
                Processing falls within one day of a major holiday.
              </div>
            )}
            <label style={broilerLabel}>
              Confirmed Hatch Date
              <input
                aria-label="Confirmed Hatch Date"
                style={broilerControl}
                type="date"
                value={form.hatchDate}
                onChange={(event) => upd('hatchDate', event.target.value)}
              />
            </label>
            {targetHatch && !form.hatchDate && (
              <div>
                <div className="poultry-meta">Suggested Hatch Dates</div>
                <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                  {hatchSuggestions.map((suggestion) => (
                    <Button key={suggestion.iso} onClick={() => upd('hatchDate', suggestion.iso)}>
                      {suggestion.day} {suggestion.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {hatchWarn && (
              <div style={{color: 'var(--warn-ink)', fontSize: 12}}>Hatch falls within one day of a major holiday.</div>
            )}
            <label style={broilerLabel}>
              Birds Ordered
              <input
                aria-label="Birds Ordered"
                style={broilerControl}
                type="number"
                min="0"
                value={form.birdCount}
                onChange={(event) => upd('birdCount', event.target.value)}
              />
            </label>
            <div style={broilerLabel}>
              Brooder
              <Segmented
                aria-label="Brooder"
                options={BROODERS.map((brooder) => ({value: brooder, label: 'B' + brooder}))}
                value={form.brooder}
                onChange={(next) => upd('brooder', next)}
              />
            </div>
            <label style={broilerLabel}>
              Days In Brooder
              {/* A stored out-of-range value keeps its own segment, marked
                  "(not allowed)", so an existing draft still shows what it
                  holds instead of silently reading as nothing selected. */}
              <Segmented
                aria-label="Days In Brooder"
                aria-invalid={brooderDaysInvalid || undefined}
                options={[
                  ...(brooderDaysInvalid
                    ? [{value: brooderDaysChoice, label: `${brooderDaysChoice} (not allowed)`}]
                    : []),
                  ...Array.from({length: BROODER_DAYS_MAX - BROODER_DAYS_MIN + 1}, (_, i) => BROODER_DAYS_MIN + i).map(
                    (days) => ({value: String(days), label: `${days} days`}),
                  ),
                ]}
                value={brooderDaysChoice}
                onChange={(next) => upd('brooderDays', next)}
              />
              <span className="poultry-meta">
                {brooderDaysInvalid
                  ? `Choose ${BROODER_DAYS_MIN} to ${BROODER_DAYS_MAX} days in the brooder.`
                  : 'Extra days come out of schooner time.'}
              </span>
              {tl && weekendSchoonerMove && (
                <span style={brooderDaysWarning}>Schooner move falls on a weekend ({fmt(tl.schoonerIn)}).</span>
              )}
            </label>
            <div style={broilerLabel}>
              Schooner
              <Segmented
                aria-label="Schooner"
                options={SCHOONERS.map((schooner) => ({value: schooner, label: schooner}))}
                value={form.schooner}
                onChange={(next) => upd('schooner', next)}
              />
            </div>
            {/* Schedule - the dates the choices above just produced, shown
                in the modal so nobody has to open the chart to see what they
                made. Deliberately NOT a PhaseBar: that primitive speaks
                past/current/done, and a batch being created has no current
                phase, so every segment would be a claim about where the birds
                are. Plain rows say the same thing honestly.

                The panel and its chart link render ALWAYS; only the rows wait
                for a timeline. A draft with no processing date yet has no
                schedule to show, but that is exactly when someone opens the
                chart to find a free slot - gating the link on `tl` took it
                away at the one moment it was most wanted. CI caught that. */}
            <div data-broiler-schedule-preview="1" style={schedulePreviewS}>
              <div style={{display: 'flex', alignItems: 'baseline', gap: 8}}>
                <span style={{fontSize: 12, fontWeight: 700, color: 'var(--label)', letterSpacing: '.06em'}}>
                  SCHEDULE
                </span>
                <Button
                  style={{
                    marginLeft: 'auto',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--brand)',
                    padding: 0,
                  }}
                  onClick={() => setPreviewOpen(true)}
                >
                  Full timeline ›
                </Button>
              </div>
              {tl ? (
                <>
                  <div style={schedulePreviewRowS}>
                    <span style={{fontWeight: 600}}>{'Brooder ' + form.brooder}</span>
                    <span>
                      {fmt(lockedBrooderIn)} → {fmt(lockedBrooderOut)}
                      {brooderDurationDays != null ? ' · ' + brooderDurationDays + ' days' : ''}
                    </span>
                  </div>
                  <div style={schedulePreviewRowS}>
                    <span style={{fontWeight: 600}}>{'Schooner ' + form.schooner}</span>
                    <span>
                      {fmt(tl.schoonerIn)} → {fmt(tl.schoonerOut)}
                      {' · ' + daysBetween(tl.schoonerIn, tl.schoonerOut) + ' days'}
                    </span>
                  </div>
                  <div style={schedulePreviewRowS}>
                    <span style={{fontWeight: 600}}>Process</span>
                    <span>{form.processingDate ? fmt(form.processingDate) : '—'}</span>
                  </div>
                </>
              ) : (
                <div style={{fontSize: 13, color: 'var(--ink-faint)'}}>Add a processing date to see the schedule.</div>
              )}
            </div>
            {conflicts.length > 0 && (
              <InlineNotice
                notice={{kind: 'warning', message: conflicts.map((conflict) => conflict.message).join('\n')}}
              />
            )}
          </div>
        </Modal>
        {previewOpen && (
          <Modal
            title="Timeline Preview"
            size="lg"
            onClose={() => setPreviewOpen(false)}
            footer={
              <div style={dialogFooterStyle}>
                <Button style={{marginLeft: 'auto'}} onClick={() => setPreviewOpen(false)}>
                  Close
                </Button>
              </div>
            }
          >
            <TimelinePanel timeline={tl ? {...tl, brooderIn: lockedBrooderIn} : null} hatchDate={form.hatchDate} />
            {timelinePreview}
          </Modal>
        )}
      </>
    );

  return (
    // The mode is exposed so a browser test can tell the full-screen form from
    // the record page's embedded one. They are different surfaces with
    // different owners.
    <div data-broiler-form-mode={embedded ? 'embedded' : 'fullscreen'}>
      {!embedded && <Header />}
      <div
        style={{
          background: embedded ? 'transparent' : 'rgba(0,0,0,.45)',
          minHeight: embedded ? 'auto' : '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: embedded ? '0' : '1.5rem 1rem',
        }}
        className="no-print"
      >
        <div
          style={{
            background: embedded ? 'transparent' : 'white',
            borderRadius: embedded ? 0 : 12,
            width: '100%',
            maxWidth: embedded ? 1060 : 960,
            border: embedded ? 'none' : '1px solid var(--border)',
            marginBottom: embedded ? 0 : '2rem',
          }}
        >
          {/* Sticky header: title + batch name + close */}
          {!embedded && (
            <div
              style={{
                padding: '12px 20px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 10,
                boxShadow: '0 1px 4px rgba(0,0,0,.06)',
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
                <div style={{fontSize: 13, fontWeight: 500, color: 'var(--ink-faint)', flexShrink: 0}}>
                  {editId ? 'Edit Batch' : 'Add New Batch'}
                </div>
                {form.name && (
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#187A54',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {form.name}
                  </div>
                )}
              </div>
              <Button
                onClick={() => {
                  if (typeof onClose === 'function') onClose();
                  else closeForm();
                }}
                style={{flexShrink: 0}}
              >
                ×
              </Button>
            </div>
          )}

          <div
            style={{
              padding: embedded ? 0 : '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: embedded ? 16 : 12,
            }}
          >
            <InlineNotice notice={formNotice} onDismiss={() => setFormNotice(null)} />
            {/* Conflict alert — only shown while the batch can still be
                rescheduled. Once a batch is processed the scheduling is
                history and the warning has nothing actionable to do. */}
            {conflicts.length > 0 &&
              form.status !== 'processed' &&
              (() => {
                const hard = conflicts.filter((c) => !c.soft);
                const soft = conflicts.filter((c) => c.soft);
                const hasHard = hard.length > 0;
                return (
                  <div
                    style={{
                      background: hasHard ? '#fef2f2' : '#fffbeb',
                      border: '1px solid ' + (hasHard ? '#F09595' : '#fde68a'),
                      borderRadius: 10,
                      padding: '10px 14px',
                    }}
                  >
                    {hasHard && (
                      <>
                        <div style={{color: '#791F1F', fontWeight: 600, fontSize: 13, marginBottom: 4}}>
                          {'\u26a0 Scheduling conflict detected:'}
                        </div>
                        {hard.map((c, i) => (
                          <div key={'h' + i} style={{color: '#b91c1c', fontSize: 12, marginTop: 3}}>
                            {'\u2022 ' + c.message}
                          </div>
                        ))}
                      </>
                    )}
                    {soft.length > 0 && (
                      <div
                        style={{
                          marginTop: hasHard ? 10 : 0,
                          paddingTop: hasHard ? 10 : 0,
                          borderTop: hasHard ? '1px solid #F09595' : 'none',
                        }}
                      >
                        <div style={{color: '#92400e', fontWeight: 600, fontSize: 13, marginBottom: 4}}>
                          {'\u26a0 Layer batch overlap (soft warning, save will go through):'}
                        </div>
                        {soft.map((c, i) => (
                          <div key={'s' + i} style={{color: '#92400e', fontSize: 12, marginTop: 3}}>
                            {'\u2022 ' + c.message}
                          </div>
                        ))}
                      </div>
                    )}
                    {hasHard && (
                      <div
                        style={{
                          marginTop: 10,
                          paddingTop: 10,
                          borderTop: '1px solid #F09595',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{fontSize: 12, color: '#791F1F', flex: 1}}>
                          You can override and save anyway if you know what you're doing (e.g. staggered timing, special
                          arrangement).
                        </div>
                        <Button
                          onClick={() =>
                            confirmActionPrompt(
                              'Save this batch with the listed booking conflicts?',
                              () => {
                                submit(true);
                              },
                              'Save Anyway',
                            )
                          }
                        >
                          Save Anyway
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            <SectionCard
              icon="schedule"
              title="Schedule"
              hint="Target processing date drives the whole grow-out timeline"
            >
              <div>
                <StepHeading number="1">Enter your target processing date</StepHeading>
                <div data-mobile-1col="1" style={{...broilerGrid2, marginBottom: 8}}>
                  <div>
                    {/* CP0 A12.1: field labels are plain (no program color) on the shared
                      recordFieldLabel primitive; program accent is reserved for the
                      date-mode selected pill below. */}
                    <label style={broilerLabel}>Breed</label>
                    <select style={broilerControl} value={form.breed} onChange={(e) => upd('breed', e.target.value)}>
                      <option value="CC">
                        Cornish Cross {'\u2014'} {growoutWeeksFor('CC', form.growoutWeeks)} weeks
                      </option>
                      <option value="WR">
                        White Ranger {'\u2014'} {growoutWeeksFor('WR', form.growoutWeeks)} weeks
                      </option>
                      {form.status === 'processed' && showLegacy && (
                        <>
                          <option disabled value="__sep__">
                            {'\u2500\u2500\u2500 Legacy \u2500\u2500\u2500'}
                          </option>
                          {LEGACY_BREEDS.map((lb) => (
                            <option key={lb.code} value={lb.code}>
                              {lb.label} (legacy)
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    {form.status === 'processed' && (
                      <Button
                        aria-pressed={showLegacy}
                        type="button"
                        onClick={() => setShowLegacy((s) => !s)}
                        style={{
                          borderColor: showLegacy ? 'var(--brand)' : 'var(--border-strong)',
                          color: showLegacy ? 'var(--brand)' : 'var(--ink)',
                        }}
                      >
                        {showLegacy ? '\u2713 Showing legacy options' : '+ Show legacy options'}
                      </Button>
                    )}
                  </div>
                  <div>
                    <label style={broilerLabel}>Processing date</label>
                    <input
                      style={broilerControl}
                      type="date"
                      value={form.processingDate}
                      onChange={(e) => upd('processingDate', e.target.value)}
                    />
                    {/* Holiday proximity is a genuine warn signal \u2014 keep warn ink. */}
                    {procWarn && (
                      <div style={{fontSize: 11, color: 'var(--warn-ink)', marginTop: 3}}>
                        {'\u26a0 Within 1 day of a major holiday'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Hatch suggestions — hidden once a hatch date is locked in. */}
                {targetHatch && !form.hatchDate && (
                  <div style={{borderTop: '1px solid var(--border)', paddingTop: 8}}>
                    <div style={{fontSize: 11, color: 'var(--text-primary)', marginBottom: 5, fontWeight: 600}}>
                      Suggested hatch dates to check with hatchery (target: {fmt(targetHatch)}):
                    </div>
                    <div style={{display: 'flex', gap: 5, flexWrap: 'wrap'}}>
                      {hatchSuggestions.map((s) => {
                        // WI-2d: selected suggestion uses the broiler program accent;
                        // unselected are neutral ghost chips (no green palette).
                        const selected = form.hatchDate === s.iso;
                        return (
                          <Button
                            aria-pressed={selected}
                            key={s.iso}
                            onClick={() => upd('hatchDate', s.iso)}
                            style={{
                              borderColor: selected ? 'var(--brand)' : 'var(--border-strong)',
                              color: selected ? 'var(--brand)' : 'var(--ink)',
                            }}
                          >
                            {s.day} {s.label}
                            {s.offset === 0
                              ? ' (exact)'
                              : s.offset < 0
                                ? ` (${Math.abs(s.offset)}d early)`
                                : ` (${s.offset}d late)`}
                          </Button>
                        );
                      })}
                    </div>
                    <div style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 5}}>
                      Click a date or type one below once confirmed with hatchery
                    </div>
                  </div>
                )}
              </div>
              <div>
                <StepHeading number="2" style={{marginTop: 22}}>
                  Confirm hatch date with hatchery
                </StepHeading>
                <div>
                  <label style={broilerLabel}>Confirmed hatch date</label>
                  <input
                    style={broilerControl}
                    type="date"
                    value={form.hatchDate}
                    onChange={(e) => upd('hatchDate', e.target.value)}
                  />
                  {/* Holiday proximity is a genuine warn signal — keep warn ink. */}
                  {hatchWarn && (
                    <div style={{fontSize: 11, color: 'var(--warn-ink)', marginTop: 3}}>
                      ⚠ Within 1 day of a major holiday
                    </div>
                  )}
                </div>
                <TimelinePanel timeline={tl ? {...tl, brooderIn: lockedBrooderIn} : null} hatchDate={form.hatchDate} />
              </div>
            </SectionCard>
            <SectionCard icon="details" title="Batch Details" bodyStyle={broilerGrid2}>
              <div>
                <label style={broilerLabel}>Batch name</label>
                <input
                  style={broilerControl}
                  value={form.name}
                  onChange={(e) => upd('name', e.target.value)}
                  placeholder="e.g. 26-01 CC BROILERS"
                />
              </div>
              <>
                <div>
                  <label style={broilerLabel}>Hatchery</label>
                  <select
                    style={broilerControl}
                    value={form.hatchery}
                    onChange={(e) => upd('hatchery', e.target.value)}
                  >
                    {hatcheries.map((h) =>
                      h === '__SEP__' ? (
                        <option key="sep" disabled value="__sep__">
                          {'\u2500\u2500\u2500 Legacy \u2500\u2500\u2500'}
                        </option>
                      ) : (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div>
                  <label style={broilerLabel}>Birds ordered</label>
                  <input
                    style={broilerControl}
                    type="number"
                    value={form.birdCount || ''}
                    onChange={(e) => upd('birdCount', e.target.value)}
                  />
                  <div style={broilerHelp}>Standard 750 · Schooner 1 solo: 650</div>
                </div>
                <div>
                  <label style={broilerLabel}>Birds arrived</label>
                  <input
                    style={broilerControl}
                    type="number"
                    value={form.birdCountActual || ''}
                    onChange={(e) => upd('birdCountActual', e.target.value)}
                    placeholder="Enter actual count"
                  />
                  <div style={broilerHelp}>
                    Actual day-1 count after hatchery overship. Enter manually — never auto-fills from ordered.
                  </div>
                </div>
                <div>
                  <label style={broilerLabel}>Chick purchase cost ($)</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.chickCost || ''}
                    onChange={(e) => upd('chickCost', e.target.value)}
                    placeholder="Total paid to hatchery"
                  />
                  <div style={broilerHelp}>
                    Total paid to the hatchery for this batch's chicks. Rolls into Total Cost.
                  </div>
                </div>

                <div>
                  <label style={broilerLabel}>Brooder assigned</label>
                  <select style={broilerControl} value={form.brooder} onChange={(e) => upd('brooder', e.target.value)}>
                    {BROODERS.map((b) => (
                      <option key={b} value={b}>
                        Brooder {b} — max 750 birds
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={brooderDaysLabel} htmlFor="broiler-brooder-days">
                    Days in brooder
                  </label>
                  <select
                    id="broiler-brooder-days"
                    data-broiler-brooder-days="1"
                    style={brooderDaysInvalid ? brooderDaysControlInvalid : brooderDaysControl}
                    value={brooderDaysChoice}
                    aria-invalid={brooderDaysInvalid || undefined}
                    aria-describedby={
                      (brooderDaysInvalid ? 'broiler-brooder-days-error' : 'broiler-brooder-days-help') +
                      (tl && weekendSchoonerMove ? ' broiler-brooder-days-weekend' : '')
                    }
                    onChange={(e) => upd('brooderDays', e.target.value)}
                  >
                    {brooderDaysInvalid && (
                      <option value={brooderDaysChoice} disabled>
                        {brooderDaysChoice} (not allowed)
                      </option>
                    )}
                    {Array.from({length: BROODER_DAYS_MAX - BROODER_DAYS_MIN + 1}, (_, i) => BROODER_DAYS_MIN + i).map(
                      (d) => (
                        <option key={d} value={String(d)}>
                          {d} days
                        </option>
                      ),
                    )}
                  </select>
                  {brooderDaysInvalid ? (
                    // I11: the error replaces the help line, phrased as the fix.
                    <div id="broiler-brooder-days-error" data-broiler-brooder-days-error="1" style={brooderDaysError}>
                      {`Choose ${BROODER_DAYS_MIN} to ${BROODER_DAYS_MAX} days in the brooder.`}
                    </div>
                  ) : tl && brooderDurationDays !== null ? (
                    <div id="broiler-brooder-days-help" data-broiler-brooding-duration="1" style={brooderDaysHelp}>
                      Move planned {brooderDurationDays} days after hatch: {fmt(tl.brooderIn)} to {fmt(tl.brooderOut)}.
                      Extra days come out of schooner time.
                    </div>
                  ) : (
                    <div id="broiler-brooder-days-help" style={brooderDaysHelp}>
                      Extra days come out of schooner time.
                    </div>
                  )}
                  {tl && weekendSchoonerMove && (
                    <div
                      id="broiler-brooder-days-weekend"
                      data-broiler-weekend-schooner-move="1"
                      style={brooderDaysWarning}
                    >
                      {`⚠ Schooner move falls on a ${WEEKDAY_NAMES[new Date(tl.schoonerIn + 'T12:00:00').getDay()]} (${fmt(tl.schoonerIn)}). Moves are usually Monday to Thursday.`}
                    </div>
                  )}
                </div>
                <div>
                  <label style={broilerLabel}>Schooner assigned</label>
                  <select
                    style={broilerControl}
                    value={form.schooner}
                    onChange={(e) => upd('schooner', e.target.value)}
                  >
                    {SCHOONERS.map((s) => (
                      <option key={s} value={s}>
                        Schooner {s}
                        {s === '1' ? ' (solo / 650 birds)' : ' (pair)'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={broilerLabel}>Status</label>
                  <select style={broilerControl} value={form.status} onChange={(e) => upd('status', e.target.value)}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {processingStatusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
              <div style={{gridColumn: '1 / -1'}}>
                <label style={broilerLabel}>Notes</label>
                <textarea
                  style={broilerTextarea}
                  value={form.notes}
                  onChange={(e) => upd('notes', e.target.value)}
                  rows={2}
                  placeholder="Farm team, transporter, distribution notes…"
                />
              </div>
            </SectionCard>
            <SectionCard icon="housing" title="Brooder & Schooner" bodyStyle={broilerGrid3}>
              <>
                <div>
                  <label style={broilerLabel}>Date In Brooder</label>
                  <div data-broiler-brooder-in-readonly="1" style={readOnlyValueBox}>
                    {fmtMDY(lockedBrooderIn)}
                  </div>
                  <div style={broilerHelp}>Locked from the Schedule dates.</div>
                </div>
                <div>
                  <label style={broilerLabel}>Date Out of Brooder</label>
                  <div data-broiler-brooder-out-readonly="1" style={readOnlyValueBox}>
                    {fmtMDY(lockedBrooderOut)}
                  </div>
                  <div style={broilerHelp}>Locked from the Schedule dates.</div>
                </div>
                <div>
                  <label style={broilerLabel}>4-Week Weight (lbs)</label>
                  <div data-broiler-week4-weight-readonly="1" style={weighInSourcedValueBox}>
                    {broilerWeekWeightLabel(week4WeightDisplay)}
                  </div>
                  <div style={weighInSourcedHint}>Pulled from completed Week 4 weigh-ins.</div>
                </div>
                <div>
                  <label style={broilerLabel}>6-Week Weight (lbs)</label>
                  <div data-broiler-week6-weight-readonly="1" style={weighInSourcedValueBox}>
                    {broilerWeekWeightLabel(week6WeightDisplay)}
                  </div>
                  <div style={weighInSourcedHint}>Pulled from completed Week 6 weigh-ins.</div>
                </div>
                {(() => {
                  const stats = calcBroilerStatsFromDailys(form, broilerDailys);
                  if (stats.legacy) {
                    return (
                      <div>
                        <label style={broilerLabel}>Mortality Cumulative</label>
                        <input
                          style={broilerControl}
                          type="number"
                          min="0"
                          value={form.mortalityCumulative || ''}
                          onChange={(e) => upd('mortalityCumulative', e.target.value)}
                        />
                      </div>
                    );
                  }
                  return (
                    <div>
                      <label style={broilerLabel}>Mortality (from daily reports)</label>
                      <div
                        style={{
                          padding: '8px 11px',
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          fontSize: 13,
                          fontWeight: 600,
                          // Mortality > 0 is a genuine danger signal (WI-2a keep).
                          color: stats.mortality > 0 ? 'var(--danger)' : 'var(--ink-faint)',
                        }}
                      >
                        {stats.mortality.toLocaleString()}
                        {stats.mortPct > 0 ? (
                          <span style={{fontWeight: 400, color: 'var(--ink-faint)', marginLeft: 6}}>
                            ({stats.mortPct.toFixed(1)}%)
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </>
            </SectionCard>
            <SectionCard icon="feed" title="Feed & Grit">
              {editId &&
                !/^b-24-/i.test(form.name) &&
                (() => {
                  const bd = broilerDailys.filter(
                    (d) =>
                      (d.batch_label || '')
                        .toLowerCase()
                        .trim()
                        .replace(/^\(processed\)\s*/, '')
                        .trim() === form.name.toLowerCase().trim(),
                  );
                  const allLabels = [
                    ...new Set(
                      broilerDailys.map((d) => d.batch_label).filter((label): label is string => Boolean(label)),
                    ),
                  ].sort();
                  // THE WAIT IS NOW A STATE OF ITS OWN, AND THAT IS THE ONE
                  // BEHAVIOUR THIS LOCALIZATION CHANGES ON PURPOSE.
                  //
                  // "Daily records not loaded yet" used to be reachable only
                  // when App's boot had not published the history, so an empty
                  // array meant "not yet" and closing/reopening the form really
                  // did help. The history is this surface's own read now and it
                  // resolves on every mount, so an in-flight query would fire
                  // that red banner — with advice that would only restart the
                  // same load — for the first few hundred milliseconds of every
                  // form open. It is held until the read has SETTLED. What it
                  // then reports is unchanged: a settled-but-empty history is
                  // still the "not loaded" case, because the resource degrades
                  // a failed load to empty exactly as App's boot did.
                  if (broilerDailysLoading) return null;
                  if (broilerDailys.length === 0)
                    return (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#b91c1c',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 10,
                          padding: '5px 10px',
                          marginBottom: 8,
                        }}
                      >
                        ⚠ Daily records not loaded yet — try closing and reopening this form.
                      </div>
                    );
                  if (bd.length === 0)
                    return (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#b91c1c',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 10,
                          padding: '5px 10px',
                          marginBottom: 8,
                        }}
                      >
                        ⚠ No daily reports found matching "{form.name}". Labels in DB:{' '}
                        {allLabels.filter((l) => l.toLowerCase().includes('26-0')).join(', ') || 'none found'}
                      </div>
                    );
                  return (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#187A54',
                        background: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        borderRadius: 10,
                        padding: '5px 10px',
                        marginBottom: 8,
                      }}
                    >
                      Auto-filled from {bd.length} daily reports. Update daily reports to change these totals.
                    </div>
                  );
                })()}
              {(() => {
                const stats = calcBroilerStatsFromDailys(form, broilerDailys);
                if (stats.legacy) {
                  // Legacy B-24-* batches: keep editable fields (no daily reports exist)
                  return (
                    <div data-mobile-1col="1" style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10}}>
                      <div>
                        <label style={broilerLabel}>Brooder Feed (lbs)</label>
                        <input
                          style={broilerControl}
                          type="number"
                          min="0"
                          value={form.brooderFeedLbs || ''}
                          onChange={(e) => upd('brooderFeedLbs', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={broilerLabel}>Schooner Feed (lbs)</label>
                        <input
                          style={broilerControl}
                          type="number"
                          min="0"
                          value={form.schoonerFeedLbs || ''}
                          onChange={(e) => upd('schoonerFeedLbs', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={broilerLabel}>Grit (lbs)</label>
                        <input
                          style={broilerControl}
                          type="number"
                          min="0"
                          value={form.gritLbs || ''}
                          onChange={(e) => upd('gritLbs', e.target.value)}
                        />
                      </div>
                    </div>
                  );
                }
                // Modern batches: read-only display sourced live from daily reports
                const ro = (label: string, val: number, suffix: string) => (
                  <div>
                    <label style={broilerLabel}>
                      {label} <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>(from daily reports)</span>
                    </label>
                    <div
                      style={{
                        padding: '8px 11px',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        // WI-2a: feed lbs is a raw metric, not a good/bad signal \u2192 black.
                        color: val > 0 ? 'var(--text-primary)' : 'var(--ink-faint)',
                      }}
                    >
                      {val > 0 ? val.toLocaleString() + suffix : '\u2014'}
                    </div>
                  </div>
                );
                return (
                  <div data-mobile-1col="1" style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10}}>
                    {ro('Starter Feed', stats.starterFeed, ' lbs')}
                    {ro('Grower Feed', stats.growerFeed, ' lbs')}
                    {ro('Grit', stats.gritLbs, ' lbs')}
                  </div>
                );
              })()}
              {/* FEED COST RATES (read-only — set in Admin → Feed Costs, propagated to all active broiler batches) */}
              <div style={{gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10}}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--ink-muted)',
                    letterSpacing: 0.5,
                    marginBottom: 6,
                  }}
                >
                  Feed Cost Rates{' '}
                  <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>
                    {'(locked \u2014 set in Admin \u203a Feed Costs)'}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    fontSize: 12,
                    color: 'var(--ink)',
                    padding: '8px 12px',
                    background: 'var(--surface-2)',
                    borderRadius: 10,
                    border: '1px solid var(--border)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    Starter:{' '}
                    <strong>
                      {form.perLbStarterCost !== '' && form.perLbStarterCost != null
                        ? '$' + parseFloat(form.perLbStarterCost as string).toFixed(3) + '/lb'
                        : '\u2014'}
                    </strong>
                  </span>
                  <span>
                    Grower (Standard):{' '}
                    <strong>
                      {form.perLbStandardCost !== '' && form.perLbStandardCost != null
                        ? '$' + parseFloat(form.perLbStandardCost as string).toFixed(3) + '/lb'
                        : '\u2014'}
                    </strong>
                  </span>
                  <span>
                    Grit:{' '}
                    <strong>
                      {form.perLbGritCost !== '' && form.perLbGritCost != null
                        ? '$' + parseFloat(form.perLbGritCost as string).toFixed(3) + '/lb'
                        : '\u2014'}
                    </strong>
                  </span>
                </div>
              </div>
            </SectionCard>
            <SectionCard
              icon="processing"
              title="Processing"
              hint="Enter once the batch comes back from the processor"
              bodyStyle={broilerGrid2}
            >
              <>
                <div>
                  <label style={broilerLabel}>Birds to Processor</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    value={form.totalToProcessor || ''}
                    onChange={(e) => upd('totalToProcessor', e.target.value)}
                  />
                </div>
                <div>
                  <label style={broilerLabel}>Processing Cost ($)</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.processingCost || ''}
                    onChange={(e) => upd('processingCost', e.target.value)}
                  />
                </div>
                <div>
                  <label style={broilerLabel}>Feed per Bird (lbs)</label>
                  <div
                    style={{
                      padding: '8px 10px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      // WI-2a: feed-per-bird is a raw metric, not a good/bad signal → black.
                      color: (() => {
                        const tf =
                          (parseFloat(form.brooderFeedLbs as string) || 0) +
                          (parseFloat(form.schoonerFeedLbs as string) || 0);
                        const p = parseFloat(form.totalToProcessor as string) || 0;
                        return tf > 0 && p > 0 ? 'var(--text-primary)' : 'var(--ink-faint)';
                      })(),
                    }}
                  >
                    {(() => {
                      const tf =
                        (parseFloat(form.brooderFeedLbs as string) || 0) +
                        (parseFloat(form.schoonerFeedLbs as string) || 0);
                      const p = parseFloat(form.totalToProcessor as string) || 0;
                      return tf > 0 && p > 0
                        ? (tf / p).toFixed(1) + ' lbs/bird'
                        : '\u2014 (enter feed totals + birds to processor)';
                    })()}
                  </div>
                </div>
                <div>
                  <label style={broilerLabel}>Avg Breast (lbs)</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.avgBreastLbs || ''}
                    onChange={(e) => upd('avgBreastLbs', e.target.value)}
                  />
                </div>
                <div>
                  <label style={broilerLabel}>Avg Thighs (lbs)</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.avgThighsLbs || ''}
                    onChange={(e) => upd('avgThighsLbs', e.target.value)}
                  />
                </div>
                <div style={{gridColumn: '1/-1'}}>
                  <label style={broilerLabel}>Avg Dressed Bird (lbs)</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.avgDressedLbs || ''}
                    onChange={(e) => upd('avgDressedLbs', e.target.value)}
                  />
                </div>
              </>
            </SectionCard>
            <SectionCard icon="totals" title="Production Totals" bodyStyle={broilerGrid2}>
              <>
                <div>
                  <label style={broilerLabel}>Total Lbs — Whole Birds</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.totalLbsWhole || ''}
                    onChange={(e) => upd('totalLbsWhole', e.target.value)}
                  />
                </div>
                <div>
                  <label style={broilerLabel}>Total Lbs — Cuts</label>
                  <input
                    style={broilerControl}
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.totalLbsCuts || ''}
                    onChange={(e) => upd('totalLbsCuts', e.target.value)}
                  />
                </div>
              </>
            </SectionCard>
            {editId && (
              <SectionCard icon="documents" title="Documents">
                {parsedProcessor && (
                  <Modal
                    title="Apply Processor Data"
                    size="md"
                    onClose={() => setParsedProcessor(null)}
                    footer={
                      <ModalFooter
                        primaryLabel="Apply Selected"
                        onPrimary={() => {
                          const updates: Partial<BatchFormDraft> = {};
                          [
                            {key: 'avgDressed' as ProcessorMetricKey, formKey: 'avgDressedLbs' as keyof BatchFormDraft},
                            {key: 'avgBreast' as ProcessorMetricKey, formKey: 'avgBreastLbs' as keyof BatchFormDraft},
                            {key: 'avgThigh' as ProcessorMetricKey, formKey: 'avgThighsLbs' as keyof BatchFormDraft},
                            {
                              key: 'totalLbsWhole' as ProcessorMetricKey,
                              formKey: 'totalLbsWhole' as keyof BatchFormDraft,
                            },
                            {
                              key: 'totalLbsCuts' as ProcessorMetricKey,
                              formKey: 'totalLbsCuts' as keyof BatchFormDraft,
                            },
                          ].forEach(({key, formKey}) => {
                            const cb = document.getElementById(`pp_${key}`) as HTMLInputElement | null;
                            if (cb && cb.checked && parsedProcessor[key] != null)
                              updates[formKey] = parsedProcessor[key];
                          });
                          setForm((f) => ({...f, ...updates}));
                          setParsedProcessor(null);
                        }}
                      />
                    }
                  >
                    <p className="poultry-meta">{parsedProcessor.fileName}</p>
                    <div style={{overflowX: 'auto'}}>
                      <table className="poultry-import-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Current</th>
                            <th>New</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(
                            [
                              {key: 'avgDressed', field: 'avgDressedLbs', label: 'Avg Dressed Weight'},
                              {key: 'avgBreast', field: 'avgBreastLbs', label: 'Avg Breast Weight'},
                              {key: 'avgThigh', field: 'avgThighsLbs', label: 'Avg Thigh Weight'},
                              {key: 'totalLbsWhole', field: 'totalLbsWhole', label: 'Whole Birds'},
                              {key: 'totalLbsCuts', field: 'totalLbsCuts', label: 'Cuts'},
                            ] as Array<{key: ProcessorMetricKey; field: keyof BatchFormDraft; label: string}>
                          )
                            .filter(({key}) => parsedProcessor[key] != null)
                            .map(({key, field, label}) => (
                              <tr key={key}>
                                <td>
                                  <label>
                                    <input type="checkbox" defaultChecked id={`pp_${key}`} style={recordCheckbox} />{' '}
                                    {label}
                                  </label>
                                </td>
                                <td>{form[field] != null && form[field] !== '' ? String(form[field]) + ' lb' : '—'}</td>
                                <td>{parsedProcessor[key]} lb</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </Modal>
                )}

                {/* Drop zone */}
                <label
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.background = '#ecfdf5';
                    e.currentTarget.style.borderColor = '#187A54';
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.background = 'var(--surface-2)';
                    e.currentTarget.style.borderColor = 'var(--border-strong)';
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.currentTarget.style.background = 'var(--surface-2)';
                    e.currentTarget.style.borderColor = 'var(--border-strong)';
                    setFormNotice(null);
                    const files = Array.from(e.dataTransfer.files).filter((f) => /\.(pdf|xlsx|xls|csv)$/i.test(f.name));
                    if (!files.length) {
                      setFormNotice({kind: 'error', message: 'Only PDF, Excel, and CSV files are supported.'});
                      return;
                    }
                    setDocUploading(true);
                    const errors = [];
                    for (const file of files) {
                      if (file.size > 20 * 1024 * 1024) {
                        errors.push(file.name + ' is over 20 MB and was skipped.');
                        continue;
                      }
                      if (/\.xlsx?$/i.test(file.name)) await parseProcessorXlsx(file);
                      try {
                        const path = `broiler-batches/${editId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                        const {error: upErr} = await sb.storage
                          .from('batch-documents')
                          .upload(path, file, {cacheControl: '3600', upsert: false});
                        if (upErr) throw upErr;
                        const doc = {
                          name: file.name,
                          path,
                          size: file.size,
                          uploadedAt: new Date().toISOString(),
                        };
                        setForm((f) => {
                          const newDocs = [...(f.documents || []), doc];
                          // The document save is confirmed before the draft is
                          // treated as saved; a refusal keeps the attachment in
                          // the draft for retry and says so out loud.
                          void persistDocuments(editId, newDocs).then((saved) => {
                            if (!saved)
                              setFormNotice({
                                kind: 'error',
                                message: 'Not Saved. Your document list is still here. Retry the save.',
                              });
                          });
                          return {...f, documents: newDocs};
                        });
                      } catch (err) {
                        const error = err as {message?: unknown};
                        errors.push('Upload failed for ' + file.name + ': ' + (error.message || 'Unknown error'));
                      }
                    }
                    setDocUploading(false);
                    if (errors.length) setFormNotice({kind: 'error', message: errors.join('\n')});
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '20px',
                    background: 'var(--surface-2)',
                    border: '2px dashed var(--border-strong)',
                    borderRadius: 10,
                    cursor: docUploading ? 'not-allowed' : 'pointer',
                    marginBottom: 10,
                    transition: 'all .15s',
                  }}
                >
                  {/* Q42: no emoji — the affordance is a plain glyph + words. */}
                  <span style={{fontSize: 26, fontWeight: 600, color: 'var(--ink-muted)', lineHeight: 1}}>
                    {docUploading ? '…' : '+'}
                  </span>
                  <div style={{fontSize: 12, fontWeight: 600, color: 'var(--ink)'}}>
                    {docUploading ? 'Uploading…' : 'Drop files here'}
                  </div>
                  <div style={{fontSize: 11, color: 'var(--ink-faint)'}}>
                    PDF, Excel, CSV · click to browse · Excel files scanned for processor data
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.xlsx,.xls,.csv"
                    multiple
                    style={{display: 'none'}}
                    disabled={docUploading}
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length) return;
                      setFormNotice(null);
                      setDocUploading(true);
                      const errors = [];
                      for (const file of files) {
                        if (!/\.(pdf|xlsx|xls|csv)$/i.test(file.name)) {
                          errors.push(file.name + ' is not a supported file type and was skipped.');
                          continue;
                        }
                        if (file.size > 20 * 1024 * 1024) {
                          errors.push(file.name + ' is over 20 MB and was skipped.');
                          continue;
                        }
                        if (/\.xlsx?$/i.test(file.name)) await parseProcessorXlsx(file);
                        try {
                          const path = `broiler-batches/${editId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                          const {error: upErr} = await sb.storage
                            .from('batch-documents')
                            .upload(path, file, {cacheControl: '3600', upsert: false});
                          if (upErr) throw upErr;
                          const doc = {
                            name: file.name,
                            path,
                            size: file.size,
                            uploadedAt: new Date().toISOString(),
                          };
                          setForm((f) => {
                            const newDocs = [...(f.documents || []), doc];
                            // Same confirmed-save boundary as the drop zone.
                            void persistDocuments(editId, newDocs).then((saved) => {
                              if (!saved)
                                setFormNotice({
                                  kind: 'error',
                                  message: 'Not Saved. Your document list is still here. Retry the save.',
                                });
                            });
                            return {...f, documents: newDocs};
                          });
                        } catch (err) {
                          const error = err as {message?: unknown};
                          errors.push('Upload failed for ' + file.name + ': ' + (error.message || 'Unknown error'));
                        }
                      }
                      setDocUploading(false);
                      e.target.value = '';
                      if (errors.length) setFormNotice({kind: 'error', message: errors.join('\n')});
                    }}
                  />
                </label>
                {(form.documents || []).length === 0 && (
                  <div style={{fontSize: 12, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                    No documents attached yet
                  </div>
                )}
                {((form.documents || []) as Array<BatchDocument & {path: string}>).map((doc, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      marginBottom: 6,
                    }}
                  >
                    {(() => {
                      // Q42: the document TYPE is a shared neutral Badge, not
                      // an emoji (the closed variant set, Title Case pill).
                      const ext = (doc.name || '').split('.').pop()!.toLowerCase();
                      const label = ext === 'xlsx' || ext === 'xls' ? 'Xls' : ext.toUpperCase();
                      return <Badge variant="neutral">{label}</Badge>;
                    })()}
                    <div style={{flex: 1, minWidth: 0}}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--ink)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.name}
                      </div>
                      <div style={{fontSize: 10, color: 'var(--ink-faint)'}}>
                        {doc.size ? Math.round(doc.size / 1024) + ' KB' : ''}
                        {doc.uploadedAt ? ' · ' + fmt(doc.uploadedAt) : ''}
                      </div>
                    </div>
                    <PrivateStorageLink
                      bucket="batch-documents"
                      path={doc.path}
                      legacyUrl={doc.url}
                      style={{
                        fontSize: 11,
                        color: 'var(--brand)',
                        fontWeight: 600,
                        textDecoration: 'none',
                        flexShrink: 0,
                      }}
                    >
                      View
                    </PrivateStorageLink>
                    <Button
                      onClick={() =>
                        confirmDelete('Remove this document? It cannot be recovered.', async () => {
                          try {
                            await sb.storage.from('batch-documents').remove([doc.path]);
                          } catch (_e) {
                            /* best-effort storage cleanup */
                          }
                          const newDocs = (form.documents || []).filter((_, j) => j !== i);
                          setForm((f) => ({...f, documents: newDocs}));
                          void persistDocuments(editId, newDocs).then((saved) => {
                            if (!saved)
                              setFormNotice({
                                kind: 'error',
                                message: 'Not Saved. Your document list is still here. Retry the save.',
                              });
                          });
                        })
                      }
                      style={{flexShrink: 0}}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </SectionCard>
            )}{' '}
          </div>

          <div
            style={{
              padding: embedded ? '8px 2px 0' : '12px 20px',
              borderTop: embedded ? 'none' : '1px solid var(--border)',
              display: 'flex',
              gap: embedded ? 14 : 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {editId ? (
              // App owns the delete confirmation. Nothing here closes or
              // navigates beside it: the confirmation runs in App's del, and
              // the close happens only AFTER the deletion is confirmed —
              // through this page's onDeleted completion callback, or through
              // App's own form close on the fullscreen surface. A refused
              // delete keeps the draft and the form exactly where they are.
              <Button variant="danger" onClick={() => del(editId, onDeleted)}>
                {embedded ? 'Delete Batch' : 'Delete'}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => submit(false)}>
                Add batch
              </Button>
            )}
            {editId && (
              <div style={{marginLeft: 'auto', fontSize: 13, color: 'var(--ink-faint)', fontWeight: 600}}>
                Auto-saves as you type
              </div>
            )}
            {!embedded && (
              <Button
                onClick={() => {
                  if (typeof onClose === 'function') onClose();
                  else closeForm();
                }}
              >
                {embedded ? 'Done' : 'Close'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
